// Google spreadsheet example: 
// https://docs.google.com/spreadsheets/d/1rFDx9yveliwcrqUGUGzKhEhPLvHVS78N13tcEtzq7PY/edit#gid=0

import PropTypes from 'prop-types'
import React from 'react'
import ReactDOM from 'react-dom'
import update from 'immutability-helper'
import $ from 'jquery'
import fromPairs from 'lodash/fromPairs'
import identity from 'lodash/identity'
import intersectionWith from 'lodash/intersectionWith'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import without from 'lodash/without'
import {
  Locale,
  canSpeak,
  getSupportedLocales,
  speak,
} from '../speech'
import GoogleAPI from './../GoogleAPI'
import Wordlist from './Wordlist'
import WordlistPrintedTable from './WordlistPrintedTable'
import imagesLoaded from 'imagesloaded'
import DataSource from '../DataSource';

async function populatePrintFrame(frame, element) {
  const targetDocument = frame.document
  const container = targetDocument.createElement('div')
  targetDocument.body.appendChild(container)
  await new Promise(resolve => {
    ReactDOM.render(element, container, resolve)
  })
}

async function printReactElement(element) {
  const iframe = document.createElement('iframe')
  const button = document.querySelector('.js-print-button')
  button.disabled = true
  button.style.cursor = 'wait'
  document.body.appendChild(iframe)
  iframe.contentWindow.document.write('<html><head></head><body></body></html>'); // Firefox hack to force iframe load
  await populatePrintFrame(iframe.contentWindow, element)
  // Check if all images are loaded before opening print window
  await new Promise(resolve => {
    imagesLoaded(
      iframe.contentWindow.document.querySelectorAll(
        '.print-image',
      ),
      () => resolve(),
    )
  })
  iframe.contentWindow.focus()
  iframe.contentWindow.print()
  iframe.remove()
  button.style.cursor = 'auto'
  button.disabled = false
}

function sendQuery(google, url, queryString) {
  return new Promise((resolve, reject) => {
    var query = new google.visualization.Query(url)
    query.setQuery(queryString)
    query.send(response => {
      if (response.isError()) {
        reject(
          'Error in query: ' +
            response.getMessage() +
            ' ' +
            response.getDetailedMessage(),
        )
      } else {
        resolve(response)
      }
    })
  })
}

function entryFilter(defaultLanguage, query) {
  if (query && defaultLanguage) {
    return entry =>
      entry.values[defaultLanguage] &&
      entry.values[defaultLanguage].value &&
      entry.values[defaultLanguage].value
        .toLowerCase()
        .includes(query.toLowerCase())
  } else {
    return () => true
  }
}

class WordlistContainer extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    wordlistId: PropTypes.string.isRequired,
    baseUrl: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    initiallySelectedLanguages: PropTypes.arrayOf(
      PropTypes.string,
    ),
    defaultLanguage: PropTypes.string.isRequired,
    myLists: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ),
    initiallySelectedList: PropTypes.string,
    createCustomUrl: PropTypes.string.isRequired,
    duplicateCustomUrl: PropTypes.string.isRequired,
    updateCustomUrl: PropTypes.string.isRequired,
    deleteCustomUrl: PropTypes.string.isRequired,
    userIsLoggedIn: PropTypes.bool,
  }

  static defaultProps = {
    initiallySelectedLanguages: [],
    myLists: [],
    userIsLoggedIn: false,
  }

  state = {
    listName: null,
    mode: 'browse',
    loading: true,
    loaded: false,
    languages: [],
    selectedLanguages: [],
    entries: [],
    searchQuery: '',
    showLanguagePicker: false,
    showShareUrl: false,
    entryLimitStep: 20,
    entryLimit: 0,
    speechSupportedLocales: [],
    myLists: [],
    visibleEntries: null,
    saving: false,
  }

  constructor(props) {
    super(props)
    this.state = update(this.state, {
      selectedLanguages: {
        $set: props.initiallySelectedLanguages,
      },
      entryLimit: { $set: this.state.entryLimitStep },
      myLists: { $set: props.myLists },
    })
    if (
      window.speechSynthesis &&
      window.speechSynthesis.onvoiceschanged
    ) {
      speechSynthesis.onvoiceschanged = this.handleSpeechSynthesisVoicesChanged
    }
    this.loadData().then(() => {
      if (props.initiallySelectedList) {
        this.setCurrentList(props.initiallySelectedList)
      }
    })
  }

  componentWillUnmount() {
    if (
      window.speechSynthesis &&
      window.speechSynthesis.onvoiceschanged &&
      window.speechSynthesis.onvoiceschanged ===
        this.handleSpeechSynthesisVoicesChanged
    ) {
      delete window.speechSynthesis.onvoiceschanged
    }
  }

  setCurrentList = listId => {
    let { baseUrl } = this.props
    let list = this.state.myLists.find(
      list => list.id === listId,
    )
    if (!list) {
      console.warn(
        `Could not find list with id "${listId}"`,
      )
      return
    }
    this.setState(state =>
      update(state, {
        selectedList: { $set: listId },
        selectedLanguages: { $set: list.selectedLanguages },
        visibleEntries: { $set: list.entryIds },
        listName: { $set: list.name },
      }),
    )
    window.history.replaceState(
      {},
      document.title,
      `${baseUrl}/${listId}`,
    )
  }

  unsetCurrentList = () => {
    let { baseUrl } = this.props
    this.setState(state =>
      update(state, {
        selectedList: { $set: null },
        selectedLanguages: {
          $set: this.props.initiallySelectedLanguages,
        },
        visibleEntries: { $set: null },
        listName: { $set: null },
      }),
    )
    window.history.replaceState({}, document.title, baseUrl)
  }

  handleSearchQueryInputChange = event => {
    let value = event.target.value
    this.setState(state =>
      update(state, {
        searchQuery: { $set: value },
        entryLimit: { $set: state.entryLimitStep },
        showShareUrl: { $set: false },
      }),
    )
  }

  handleShareUrlButtonClick = event => {
    event.preventDefault()
    let value = !this.state.showShareUrl
    this.setState(state =>
      update(state, {
        showShareUrl: { $set: value },
      }),
    )
  }

  handleShowLanguagePickerButtonClick = event => {
    event.preventDefault()
    let value = !this.state.showLanguagePicker
    this.setState(state =>
      update(state, {
        showLanguagePicker: { $set: value },
        showShareUrl: { $set: false },
      }),
    )
  }

  handleLanguageCheckboxChange = event => {
    let languageId = event.target.value
    let action
    if (this.state.selectedLanguages.includes(languageId)) {
      action = selectedLanguages =>
        without(selectedLanguages, languageId)
    } else {
      action = selectedLanguages =>
        selectedLanguages.concat([languageId])
    }
    this.setState(state =>
      update(state, {
        selectedLanguages: { $apply: action },
      }),
    )
  }

  handleLoadMoreButtonClick = event => {
    event.preventDefault()
    this.setState(state =>
      update(state, {
        entryLimit: {
          $apply: value => value + state.entryLimitStep,
        },
      }),
    )
  }

  handleEntrySpeakClick = (entry, language, event) => {
    event.preventDefault()
    if (
      entry.values[language.id] &&
      entry.values[language.id].value
    ) {
      speak(entry.values[language.id].value, language.code)
    }
  }

  handleSpeechSynthesisVoicesChanged = () => {
    this.setState(state =>
      update(state, {
        speechSupportedLocales: {
          $set: getSupportedLocales().map(
            locale =>
              locale.format`${'language'}_${'region'}`,
          ),
        },
      }),
    )
  }

  handleListTabClick = (listId, event) => {
    event.preventDefault()
    this.setState(state =>
      update(state, {
        mode: { $set: 'browse' },
        showShareUrl: { $set: false },
      }),
    )
    if (listId === false) {
      this.unsetCurrentList()
    } else {
      this.setCurrentList(listId)
    }
  }

  handleCreateListTabClick = event => {
    event.preventDefault()
    this.setState(state =>
      update(state, {
        mode: { $set: 'create' },
        showShareUrl: { $set: false },
      }),
    )
  }

  handleEditorFormSubmit = list => {
    let { createCustomUrl, updateCustomUrl } = this.props
    let data = Object.assign({}, list, {
      wordlist: this.props.wordlistId,
      selectedLanguages: this.state.selectedLanguages,
    })
    this.setState(state =>
      update(state, {
        saving: {
          $set: true,
        },
      }),
    )
    if (this.state.mode === 'create') {
      $.ajax(createCustomUrl, {
        beforeSend: function(xhr){
            xhr.setRequestHeader( 'X-WP-Nonce', wpApiSettings.nonce );
        },
        type: 'POST',
        data,
      })
        .done(response => {
          let id = String(response.id)
          this.setState(
            state =>
              update(state, {
                mode: { $set: 'browse' },
                saving: { $set: false },
                myLists: {
                  $push: [
                    Object.assign(data, {
                      id,
                      canEdit: true,
                    }),
                  ],
                },
              }),
            () => {
              this.setCurrentList(id)
            },
          )
        })
        .fail((jqXHR, textStatus, errorThrown) => {
          this.setState(state =>
            update(state, {
              saving: { $set: false },
            }),
          )
          alert('Kunde inte spara', textStatus)
        })
    } else if (this.state.mode === 'edit') {
      $.ajax(updateCustomUrl, {
        beforeSend: function(xhr){
            xhr.setRequestHeader( 'X-WP-Nonce', wpApiSettings.nonce );
        },
        type: 'POST',
        data,
      })
        .done(() => {
          let { id } = data
          this.setState(
            state =>
              update(state, {
                mode: { $set: 'browse' },
                saving: { $set: false },
                myLists: {
                  [state.myLists.findIndex(
                    list => list.id === id,
                  )]: {
                    $set: data,
                  },
                },
              }),
            () => {
              this.setCurrentList(id)
            },
          )
        })
        .fail(() => {
          this.setState(state =>
            update(state, {
              saving: { $set: false },
            }),
          )
          alert('Kunde inte spara')
        })
    }
  }

  handleEditorFormCancel = event => {
    event.preventDefault()
    this.setState(state =>
      update(state, {
        mode: { $set: 'browse' },
      }),
    )
  }

  handleEditButtonClick = event => {
    event.preventDefault()
    this.setState(state =>
      update(state, {
        mode: { $set: 'edit' },
      }),
    )
  }

  handleDeleteButtonClick = event => {
    event.preventDefault()
    let id = this.state.selectedList
    let { deleteCustomUrl } = this.props
    if (
      confirm(
        'Är du säker på att du vill ta bort listan permanent?',
      )
    ) {
      $.ajax(deleteCustomUrl, {
        beforeSend: function(xhr){
            xhr.setRequestHeader( 'X-WP-Nonce', wpApiSettings.nonce );
        },
        type: 'POST',
        data: {
          id,
        },
      })
        .done(() => {
          this.setState(
            state =>
              update(state, {
                mode: { $set: 'browse' },
                saving: { $set: false },
                myLists: {
                  $apply: myLists =>
                    myLists.filter(list => list.id !== id),
                },
              }),
            () => {
              this.unsetCurrentList()
            },
          )
        })
        .fail(() => {
          this.setState(state =>
            update(state, {
              saving: { $set: false },
            }),
          )
          alert('Kunde inte spara')
        })
    }
  }

  handlePrintButtonClick = event => {
    event.preventDefault()
    let props = this.makeProps()
    printReactElement(<WordlistPrintedTable {...props} />)
  }

  handleShareButtonClick = event => {
    event.preventDefault()
    const value = !this.state.showShareUrl
    this.setState(state =>
      update(state, {
        showShareUrl: { $set: value },
      }),
    )
  }

  duplicateList = () => {
    event.preventDefault()
    let id = this.state.selectedList
    let { duplicateCustomUrl } = this.props
    $.ajax(duplicateCustomUrl, {
      beforeSend: function(xhr){
        xhr.setRequestHeader( 'X-WP-Nonce', wpApiSettings.nonce );
      },
      type: 'POST',
      data: {
        id,
      },
    })
      .done(({ list }) => {
        this.setState(
          state =>
            update(state, {
              mode: { $set: 'browse' },
              saving: { $set: false },
              myLists: {
                $apply: myLists => myLists.concat([list]),
              },
            }),
          () => {
            this.setCurrentList(list.id)
          },
        )
      })
      .fail(() => {
        this.setState(state =>
          update(state, {
            saving: { $set: false },
          }),
        )
        alert('Kunde inte spara')
      })
  }

  makeProps = () => {
    let {
      defaultLanguage,
      baseUrl,
      userIsLoggedIn,
      title,
    } = this.props
    let {
      listName,
      mode,
      entries,
      languages,
      selectedLanguages,
      searchQuery,
      showShareUrl,
      showLanguagePicker,
      loading,
      loaded,
      entryLimit,
      visibleEntries,
      myLists,
      selectedList,
      saving,
    } = this.state
    let filteredEntries = entries
    if (mode === 'browse' && visibleEntries) {
      filteredEntries = intersectionWith(
        filteredEntries,
        visibleEntries,
        (entry, visibleEntry) => entry.id === visibleEntry,
      )
    }
    filteredEntries = filteredEntries.filter(
      entryFilter(defaultLanguage, searchQuery),
    )
    let props = {
      title,
      listName,
      mode,
      availableLanguages: languages,
      defaultLanguageId: defaultLanguage,
      entries: filteredEntries.slice(0, entryLimit),
      totalEntries: filteredEntries.length,
      allEntries: entries,
      selectedEntries: visibleEntries
        ? intersectionWith(
            entries,
            visibleEntries,
            (entry, visibleEntry) =>
              entry.id === visibleEntry,
          )
        : entries,
      languages: [defaultLanguage]
        .concat(selectedLanguages)
        .map(colId =>
          languages.find(
            language => language.colId === colId,
          ),
        )
        .filter(identity)
        .map(language => ({
          ...language,
          canSpeak: canSpeak(language.speechCode),
        })),
      searchQuery,
      myLists,
      selectedList,
      baseUrl,
      userIsLoggedIn,
      onSearchQueryInputChange: this
        .handleSearchQueryInputChange,
      onShowLanguagePickerButtonClick: this
        .handleShowLanguagePickerButtonClick,
      onLanguageCheckboxChange: this
        .handleLanguageCheckboxChange,
      onLoadMoreButtonClick: this.handleLoadMoreButtonClick,
      onEntrySpeakClick: this.handleEntrySpeakClick,
      onListTabClick: this.handleListTabClick,
      onCreateListTabClick: this.handleCreateListTabClick,
      onEditorFormSubmit: this.handleEditorFormSubmit,
      onEditorFormCancel: this.handleEditorFormCancel,
      onEditButtonClick: this.handleEditButtonClick,
      onDeleteButtonClick: this.handleDeleteButtonClick,
      onPrintButtonClick: this.handlePrintButtonClick,
      onShareButtonClick: this.handleShareButtonClick,
      showShareUrl,
      showLanguagePicker,
      loading,
      loaded,
      saving,
      duplicateList: this.duplicateList,
    }
    return props
  }

    loadData = async () => {
        try {
            let source = new DataSource;

            const availableLanguages = await source.init({
                id: this.props.wordlistId,
                limit: 2,
                offset: 1
            });

            let languages = range(
                2,
                availableLanguages.getNumberOfColumns(),
            ).map(i => ({
                id: availableLanguages.getColumnId(i),
                colId: availableLanguages.getColumnId(i),
                label: availableLanguages.getValue(0, i),
                code: availableLanguages.getValue(1, i),
                speechCode: availableLanguages.getValue(1, i) && Locale.parse(availableLanguages.getValue(1, i)).format`${'language'}_${'region'}`,
                index: i,
            }))

            const rows = await source.init({
                id: this.props.wordlistId,
                offset: 4
            });

            let entries = range(
                0,
                rows.getNumberOfRows(),
            ).map(i => ({
                id: rows.getValue(i, 1),
                index: i,
                values: fromPairs(
                    languages.map(language => [
                        language.id,
                        { value: (rows.getValue(i, language.index) || '').trim() },
                    ]),
                ),
            }))
            .filter(
                ({ id, values }) => id && values[this.props.defaultLanguage] && values[this.props.defaultLanguage].value,
            )

            entries = uniqBy(entries, 'id')

            this.setState(state => 
                update(state, {
                    loading: { $set: false },
                    loaded: { $set: true },
                    languages: { $set: languages },
                    entries: { $set: entries },
                })
            )
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)

            this.setState(state =>
                update(state, {
                    loading: { $set: false },
                }),
            )
            return
        }
    }

    render() {
        let props = this.makeProps()
        return <Wordlist {...props} />
    }
}

export default WordlistContainer
